import { DicomStructureSet } from '~/domain/dicom/dicomstructureset';

export interface RoiCode {
    readonly value: string;
    readonly scheme: string;
    readonly meaning?: string;
}

export interface IdentifiedRoi {
    readonly name: string;
    readonly code?: RoiCode;
}

// How a counterpart was found: 'suggested' rests on a code's meaning alone and needs the user's confirmation.
type CounterpartMatch = 'name' | 'spelling' | 'code' | 'suggested';

export interface Counterpart {
    readonly name: string;
    readonly match: CounterpartMatch;
}

type Side = 'l' | 'r' | '';

// Only a separated suffix marks a side: a bare trailing letter is part of the word.
const SIDE_SUFFIX = /[\s_\-.](l|r|left|right)$/i;
// A leading side is a whole word: a leading letter is part of the name.
const SIDE_PREFIX = /^(left|right)[\s_\-.]+/i;
const SEPARATORS = /[\s_\-.]+/g;
const MEANING_SEPARATORS = /[\s_\-.,()]+/g;
// A meaning names its structure's side first or last ("Left parotid gland", "Parotid Left").
const MEANING_SIDE = /^(left|right)\b|\b(left|right)$/i;
const FMA_SCHEMES: ReadonlySet<string> = new Set(['FMA', '99FMA']);

interface NameParts {
    readonly bare: string;
    // Undefined when the name states both sides.
    readonly side: Side | undefined;
}

function partsOfName(name: string): NameParts {
    let bare = name.trim();
    const suffix = SIDE_SUFFIX.exec(bare);
    if (suffix) {
        bare = bare.slice(0, suffix.index);
    }
    const prefix = SIDE_PREFIX.exec(bare);
    if (prefix) {
        bare = bare.slice(prefix[0].length);
    }
    const sides = [suffix?.[1], prefix?.[1]].flatMap((word) =>
        word ? [word[0].toLowerCase() as Side] : []
    );
    return {
        bare,
        side:
            sides.length === 2 && sides[0] !== sides[1]
                ? undefined
                : (sides[0] ?? ''),
    };
}

function sideOfMeaning(meaning: string | undefined): Side {
    const match = MEANING_SIDE.exec((meaning ?? '').trim());
    const word = match?.[1] ?? match?.[2];
    return word ? (word[0].toLowerCase() as Side) : '';
}

// The structure's side from its name and its code's meaning; undefined when the two disagree.
function sideOf(roi: IdentifiedRoi): Side | undefined {
    const byName = partsOfName(roi.name).side;
    const byMeaning = sideOfMeaning(roi.code?.meaning);
    if (byName === undefined || (byName && byMeaning && byName !== byMeaning)) {
        return undefined;
    }
    return byName || byMeaning;
}

function core(text: string, separators: RegExp = SEPARATORS): string {
    return text.toLowerCase().replace(separators, '');
}

// Spelling-independent form: case and separators vary by site and system, laterality does not.
function comparableForm(name: string): string | undefined {
    const { bare, side } = partsOfName(name);
    // A name stating both sides has no comparable form.
    return side === undefined ? undefined : `${core(bare)}:${side}`;
}

function meaningForm(meaning: string): string {
    const trimmed = meaning.trim();
    const match = MEANING_SIDE.exec(trimmed);
    const bare = match ? trimmed.replace(match[0], '') : trimmed;
    return `${core(bare, MEANING_SEPARATORS)}:${sideOfMeaning(trimmed)}`;
}

const SIDE_WORDS: ReadonlySet<string> = new Set(['l', 'r', 'left', 'right']);

// The words of a name or meaning, split at case changes and separators, sides left out.
function wordsOf(text: string): string[] {
    return text
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .toLowerCase()
        .split(/[^a-z0-9]+/)
        .filter((word) => word !== '' && !SIDE_WORDS.has(word));
}

function isFma(code: RoiCode): boolean {
    return FMA_SCHEMES.has(code.scheme.trim().toUpperCase());
}

function sameCode(a: RoiCode, b: RoiCode): boolean {
    return isFma(a) && isFma(b) && a.value.trim() === b.value.trim();
}

function single<T>(matches: readonly T[]): T | undefined {
    return matches.length === 1 ? matches[0] : undefined;
}

// Never a guess: each step pairs only when exactly one candidate qualifies, and sides must agree.
export function counterpartOf(
    roi: IdentifiedRoi,
    candidates: readonly IdentifiedRoi[]
): Counterpart | undefined {
    const byName = candidates.find((candidate) =>
        DicomStructureSet.structureNamesTrimmedEqual(candidate.name, roi.name)
    );
    if (byName) {
        return { name: byName.name, match: 'name' };
    }
    const form = comparableForm(roi.name);
    if (form === undefined) {
        return undefined;
    }
    const bySpelling = single(
        candidates.filter(
            (candidate) => comparableForm(candidate.name) === form
        )
    );
    if (bySpelling) {
        return { name: bySpelling.name, match: 'spelling' };
    }
    const side = sideOf(roi);
    if (side === undefined) {
        return undefined;
    }
    const sameSide = candidates.filter(
        (candidate) => sideOf(candidate) === side
    );
    const { code } = roi;
    if (code) {
        const byCode = single(
            sameSide.filter(
                (candidate) => candidate.code && sameCode(candidate.code, code)
            )
        );
        if (byCode) {
            // A side known only from the code, never from the name the user gave, is not trusted on its own.
            const sideFromCodeAlone =
                !partsOfName(roi.name).side && !!sideOfMeaning(code.meaning);
            return {
                name: byCode.name,
                match: sideFromCodeAlone ? 'suggested' : 'code',
            };
        }
    }
    const withMeaning = sameSide.filter(
        (candidate) => !!candidate.code?.meaning
    );
    const nameWords = wordsOf(roi.name);
    const bySuggestion =
        single(
            withMeaning.filter(
                (candidate) =>
                    meaningForm(candidate.code?.meaning ?? '') === form
            )
        ) ??
        // Every word of the name within the meaning, never the reverse: a more specific name is never offered a broader structure.
        single(
            withMeaning.filter((candidate) => {
                const meaningWords = wordsOf(candidate.code?.meaning ?? '');
                return (
                    nameWords.length > 0 &&
                    nameWords.every((word) => meaningWords.includes(word))
                );
            })
        );
    return bySuggestion && { name: bySuggestion.name, match: 'suggested' };
}
